import React from 'react';
import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import BannerPromotion from 'modules/promotions/banners/banner-promotion/index.jsx';
import BannerSkeletons from 'modules/promotions/banners/banner-skeletons/index.jsx';
import { range } from 'mojito/utils';

const { LoaderSuspense, FlexPane, Swiper } = MojitoPresentation.Components;
const { UIViewImplementation } = MojitoCore.Presentation;
const { useIrreversibleTrue } = MojitoPresentation.Hooks;
const isShallowEqual = MojitoCore.Base.objUtils.isShallowEqual;

export default class BannerPromotionsView extends UIViewImplementation {
    constructor(props) {
        super(props);
        this.onPromotionClicked = this.onPromotionClicked.bind(this);
        this.state = {};
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isShallowEqual(this.props, nextProps) || !isShallowEqual(this.state, nextState);
    }

    componentDidUpdate(prevProps) {
        if (this.props.promotions !== prevProps.promotions) {
            if (!this.props.promotions.length || this.shouldShowCarousel()) {
                return;
            }
            this.props.onPrepareBannersToRender(range(this.props.promotions.length));
        }
    }

    onPromotionClicked(promotion) {
        this.props.onBannerClick(promotion);
    }

    shouldShowCarousel() {
        return this.config.stacked;
    }

    renderCarousel() {
        const bannerPromotions = this.getBannerPromotions();
        return this.shouldShowCarousel() ? (
            <Swiper
                onPrepareSlidesToRender={this.props.onPrepareBannersToRender}
                config={this.config.swiper}
            >
                {bannerPromotions}
            </Swiper>
        ) : (
            bannerPromotions.map(promotionRenderer => promotionRenderer(true))
        );
    }

    getBannerPromotions() {
        return (
            this.props.promotions
                // eslint-disable-next-line react/display-name
                .map(promotion => shouldLoad => (
                    <LazyBannerPromotion
                        config={this.config.banner}
                        readyToLoad={shouldLoad}
                        promotion={promotion}
                        key={promotion.id}
                        shouldRequestData={this.props.shouldRequestData}
                        onClick={() => this.props.onBannerClick(promotion)}
                    />
                ))
        );
    }

    render() {
        const { promotionsLoaded, elementRef } = this.props;

        const skeleton = (
            <FlexPane config={this.config.skeletonsContainer} elementRef={elementRef}>
                <BannerSkeletons config={this.config.bannerSkeletons} />
            </FlexPane>
        );

        return (
            <FlexPane config={this.config.container} elementRef={elementRef}>
                <LoaderSuspense
                    isContentPending={!promotionsLoaded}
                    config={this.config.skeletonLoader}
                    loader={skeleton}
                >
                    <FlexPane config={this.config.innerContainer} class="ta-BannerPromotionsView">
                        {this.renderCarousel()}
                    </FlexPane>
                </LoaderSuspense>
            </FlexPane>
        );
    }
}

function LazyBannerPromotion(props) {
    const { config, promotion, readyToLoad, onClick, shouldRequestData } = props;
    const shouldShowPromotion = useIrreversibleTrue(readyToLoad);
    // The banners are lazy loadable. Once user is swiping the banners the swiper will inform us that banner is approaching the view port.
    // Swiper will send readyToLoad:true prop in this case. Once it is true we will send promotion object to the banner component.
    // The banner will use it to load image and render itself. The aim is not to load images until user is intended to see it.
    return (
        <BannerPromotion
            config={config}
            promotion={shouldShowPromotion ? promotion : {}}
            onClick={onClick}
            shouldRequestData={shouldRequestData}
        />
    );
}
